import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PatientPicker from '@components/Patient/PatientPicker';
import {
  describeFilter,
  FilterBar,
  type FilterFieldDef,
  type FiltersState,
  type FilterValue,
  SortMenuButton,
} from '@components/ui/FilterMenu';
import { useClinicians } from '@features/organizations';
import { mapPatientInfoResponseToPatient, usePatientInfoQuery } from '@features/patients';
import { useSubjectLanguageVariants } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { getAppScrollTop, scrollAppTo } from '@utils/appScroller';
import { useSnackbar } from 'notistack';

import type { Patient, PatientInfo } from '../../../../../types/types';
import type {
  BillingSavedView,
  ClaimWorkCounts,
  ClaimWorkItem,
  ClaimWorkSavedConfig,
  ClaimWorkSortKey,
  ClaimWorkViewKey,
} from '../../../store';
import {
  useBatchClaimSubmit,
  useClaimWorkQuery,
  useClaimWorkSavedViewsQuery,
  useCreateClaimWorkSavedViewMutation,
  useDeleteClaimWorkSavedViewMutation,
  useUpdateClaimWorkSavedViewMutation,
} from '../../../store';
import { formatDateOnly } from '../../../utils';
import {
  isWorkableClaimStage,
  WORK_STATE_COLORS,
  WORK_STATE_LABELS,
} from '../../../utils/claimWorkPresentation';
import { readWorklistReturn, workbenchProvenanceState } from '../../../utils/worklistProvenance';
import { jotBillStickyTop } from '../jotBillHeaderHeight';
import {
  type ColumnDef,
  DataTable,
  formatCents,
  Pagination,
  PatientIdentity,
  ProviderIdentity,
  type SelectionAction,
  useBillingPayerOptionsQuery,
  useColumnPreferences,
} from '../shared';

import { deriveClaimBatchRowStates } from './claimBatchRowStates';
import { ClaimBulkActionSlot } from './ClaimBulkActionSlot';
import { selectNewerClaimWorkCounts } from './claimWorkCounts';
import {
  readClaimWorkEphemera,
  updateClaimWorkEphemeraScroll,
  writeClaimWorkEphemera,
} from './claimWorkEphemera';
import {
  createDefaultClaimWorkPredicates,
  dateRangeForWindow,
  RELATIVE_DATE_WINDOWS,
  savedViewVisibleColumns,
} from './claimWorkFilters';
import {
  applyClaimWorkUrlState,
  claimWorkStateSignature,
  type ClaimWorkUrlState,
  parseClaimWorkUrlState,
} from './claimWorkUrlState';
import { useWorklistRowPrefetch } from './useWorklistRowPrefetch';

const selectedValues = (predicates: FiltersState, fieldId: string): string[] => {
  const value = predicates[fieldId];
  return value?.kind === 'multiSelect' ? value.values : [];
};

const selectedDateRange = (
  predicates: FiltersState,
): Extract<FilterValue, { kind: 'dateRange' }> | undefined => {
  const value = predicates.serviceDate;
  return value?.kind === 'dateRange' ? value : undefined;
};

const commaSeparated = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(',') : undefined;

const isPatientInfo = (patient: Patient | PatientInfo): patient is PatientInfo => 'name' in patient;

const WORK_VIEWS: Array<{ key: ClaimWorkViewKey; label: string; color: string }> = [
  { key: 'all_claim_work', label: 'All claim work', color: '#64748B' },
  { key: 'ready_to_submit', label: 'Ready to submit', color: '#2E7D32' },
  { key: 'needs_correction', label: 'Needs correction', color: '#D84315' },
  { key: 'payer_in_progress', label: 'Payer in progress', color: '#3F51B5' },
  { key: 'payment_posted', label: 'Payment posted', color: '#00897B' },
];

const SORTS: Array<{ key: ClaimWorkSortKey; label: string }> = [
  { key: 'service_date_desc', label: 'Service date · newest' },
  { key: 'service_date_asc', label: 'Service date · oldest' },
  { key: 'charge_desc', label: 'Charge · highest' },
  { key: 'charge_asc', label: 'Charge · lowest' },
];

const CLAIM_LIFECYCLES = [
  'draft',
  'processing',
  'ready',
  'submitted',
  'acknowledged',
  'accepted',
  'rejected',
  'denied',
  'paid',
  'partially_paid',
  'reversed',
];

const formatDate = (value?: string | null): string =>
  formatDateOnly(value, { day: 'numeric', month: 'short' }, '—');

const normalizeConfig = (config: ClaimWorkSavedConfig): string =>
  JSON.stringify({
    filters: {
      claim_lifecycles: config.filters?.claim_lifecycles ?? [],
      patient_ids: config.filters?.patient_ids ?? [],
      payer_ids: config.filters?.payer_ids ?? [],
      provider_ids: config.filters?.provider_ids ?? [],
      service_date_window_days: config.filters?.service_date_window_days ?? null,
      service_date_from: config.filters?.service_date_from ?? null,
      service_date_to: config.filters?.service_date_to ?? null,
    },
    sort: config.sort ?? 'service_date_desc',
    visible_columns: config.visible_columns ?? [],
    work_view: config.work_view ?? 'all_claim_work',
  });

const first = (values?: string[]): string => values?.[0] ?? '';

/* Stable empty stack for the derived page state — a fresh [] every render
   would churn the ephemera-persistence effect's deps. */
const EMPTY_CURSOR_HISTORY: Array<string | undefined> = [];

const filtersFromConfig = (config: ClaimWorkSavedConfig): FiltersState => {
  const predicates: FiltersState = {};
  const addMultiSelect = (fieldId: string, values?: string[]) => {
    if (values?.length) predicates[fieldId] = { kind: 'multiSelect', values };
  };
  addMultiSelect('claimLifecycle', config.filters?.claim_lifecycles);
  addMultiSelect('patient', config.filters?.patient_ids);
  addMultiSelect('payer', config.filters?.payer_ids);
  addMultiSelect('provider', config.filters?.provider_ids);

  if (config.filters?.service_date_window_days) {
    predicates.serviceDate = dateRangeForWindow(config.filters.service_date_window_days);
  } else if (config.filters?.service_date_from || config.filters?.service_date_to) {
    predicates.serviceDate = {
      kind: 'dateRange',
      start: config.filters.service_date_from ?? null,
      end: config.filters.service_date_to ?? null,
    };
  }
  return predicates;
};

const getDefaultColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.1fr' },
  { key: 'provider_name', label: 'Provider', width: '1.2fr' },
  { key: 'payer_name', label: 'Payer', width: '0.9fr' },
  { key: 'issues', label: 'Issues and findings', required: true, width: '1.8fr' },
  { key: 'amount', label: 'Charge', required: true, type: 'mono', align: 'right', width: '90px' },
  { key: 'work_state', label: 'State', required: true, width: '132px' },
];

export const ClaimWorkViewRail = ({
  activeSavedViewId,
  activeWorkView,
  counts,
  dirty,
  isMutating,
  savedViews,
  onDelete,
  onSave,
  onSelectSavedView,
  onSelectWorkView,
  onUpdate,
}: {
  activeSavedViewId: string | null;
  activeWorkView: ClaimWorkViewKey;
  counts: Record<string, number>;
  dirty: boolean;
  isMutating: boolean;
  savedViews: BillingSavedView[];
  onDelete: (view: BillingSavedView) => void;
  onSave: () => void;
  onSelectSavedView: (view: BillingSavedView) => void;
  onSelectWorkView: (key: ClaimWorkViewKey) => void;
  onUpdate: () => void;
}) => (
  <Card
    variant="outlined"
    /* Sticks BELOW the page header/tabs, not under them: the offset is the
       shell's MEASURED header height (GDN-3214), so editing the header can
       never silently regress this. */
    sx={{ position: { lg: 'sticky' }, top: { lg: jotBillStickyTop(16) }, width: '100%' }}
  >
    <CardContent sx={{ '&:last-child': { pb: 1.5 }, p: 1.5 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em' }}>
        CLAIM WORK
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.25 }} variant="caption">
        One operational state per claim
      </Typography>
      <Stack spacing={0.4}>
        {WORK_VIEWS.map(view => {
          const selected = activeSavedViewId === null && activeWorkView === view.key;
          return (
            <Button
              key={view.key}
              aria-pressed={selected}
              color="inherit"
              onClick={() => onSelectWorkView(view.key)}
              size="small"
              sx={{
                '&::before': {
                  bgcolor: view.color,
                  borderRadius: 4,
                  content: '""',
                  height: selected ? 20 : 7,
                  mr: 1,
                  transition: 'height 120ms ease',
                  width: 3,
                },
                bgcolor: selected ? 'action.selected' : 'transparent',
                justifyContent: 'flex-start',
                minHeight: 36,
                px: 1,
                textTransform: 'none',
              }}
            >
              <Box
                component="span"
                sx={{ flex: 1, overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis' }}
              >
                {view.label}
              </Box>
              <Typography
                color="text.secondary"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
                variant="caption"
              >
                {counts[view.key] ?? 0}
              </Typography>
            </Button>
          );
        })}
      </Stack>

      <Divider sx={{ my: 1.25 }} />
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography color="text.secondary" variant="caption">
          Organization views
        </Typography>
        <Button
          onClick={onSave}
          size="small"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </Stack>
      <Stack spacing={0.4} sx={{ mt: 0.5 }}>
        {savedViews.length === 0 && (
          <Typography color="text.secondary" sx={{ px: 1, py: 1 }} variant="caption">
            Save a filter set for everyone in this organization.
          </Typography>
        )}
        {savedViews.map(view => {
          const selected = activeSavedViewId === view.billing_saved_view_id;
          return (
            <Box key={view.billing_saved_view_id} sx={{ display: 'flex', gap: 0.25 }}>
              <Button
                aria-pressed={selected}
                color="inherit"
                onClick={() => onSelectSavedView(view)}
                size="small"
                startIcon={<SaveOutlinedIcon fontSize="small" />}
                sx={{
                  bgcolor: selected ? 'action.selected' : 'transparent',
                  color: selected ? 'primary.main' : 'text.primary',
                  flex: 1,
                  justifyContent: 'flex-start',
                  minWidth: 0,
                  px: 1,
                  textTransform: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{ flex: 1, overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis' }}
                >
                  {view.name}
                </Box>
                <Typography
                  color="text.secondary"
                  sx={{ ml: 0.5, fontVariantNumeric: 'tabular-nums' }}
                  variant="caption"
                >
                  {view.matching_count ?? 0}
                </Typography>
              </Button>
              {selected && (
                <Button
                  aria-label={`Delete ${view.name}`}
                  color="inherit"
                  disabled={isMutating}
                  onClick={() => onDelete(view)}
                  size="small"
                  sx={{ minWidth: 32, px: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
      {activeSavedViewId && dirty && (
        <Alert
          action={
            <Button color="inherit" disabled={isMutating} onClick={onUpdate} size="small">
              Update
            </Button>
          }
          icon={false}
          severity="warning"
          sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.72rem' } }}
        >
          Unsaved changes
        </Alert>
      )}
    </CardContent>
  </Card>
);

export const ClaimsTab: React.FC = () => {
  const { plural, singularCap } = useSubjectLanguageVariants();
  const { billing } = useEventTracking();
  const theme = useTheme();
  const navigate = useNavigate();
  const { clinicians, isLoading: cliniciansLoading } = useClinicians();
  const { data: payerData, isLoading: payersLoading } = useBillingPayerOptionsQuery();
  /* GDN-3196: THE URL IS THE LIST. Canonical state — work view, saved view,
     filters, sort, per-page — parses out of the query string every render,
     and every change writes back via REPLACE: reload-safe and shareable by
     construction, with zero history spam. `useState` below holds only the
     deliberately-ephemeral rest — and row selection stays out of BOTH on
     purpose: restoring a checked batch after time away invites submitting a
     selection whose members changed underneath you. */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => parseClaimWorkUrlState(searchParams), [searchParams]);
  const { perPage, predicates, savedViewId: activeSavedViewId, sort, workView } = urlState;
  const writeUrlState = useCallback(
    (change: Partial<ClaimWorkUrlState>) => {
      setSearchParams(
        prev => applyClaimWorkUrlState(prev, { ...parseClaimWorkUrlState(prev), ...change }),
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const stateSignature = useMemo(() => claimWorkStateSignature(urlState), [urlState]);

  const [selectedPatient, setSelectedPatient] = useState<Patient | PatientInfo>();
  /* Cursor stack hydrates from sessionStorage when the stored record belongs
     to THIS canonical state (best-effort — cursors are server-opaque). The
     page state REMEMBERS which signature it was built under: if the URL
     changes underneath it (browser back/forward, a pasted link), the
     mismatch derives to page 1 in the same render — a cursor never rides
     into a query it doesn't belong to. */
  const [restoredEphemera] = useState(() => readClaimWorkEphemera(stateSignature));
  const [pageState, setPageState] = useState<{
    signature: string;
    cursor: string | undefined;
    history: Array<string | undefined>;
  }>(() => ({
    signature: stateSignature,
    cursor: restoredEphemera?.cursor,
    history: restoredEphemera?.cursorHistory ?? [],
  }));
  const cursorStateCurrent = pageState.signature === stateSignature;
  const cursor = cursorStateCurrent ? pageState.cursor : undefined;
  const cursorHistory = cursorStateCurrent ? pageState.history : EMPTY_CURSOR_HISTORY;
  /* True only while a restored cursor is unconfirmed: if the server rejects
     it (expired token), fall back to page 1 of the same filtered list —
     never an error, never a loop. Cleared by the first success or the first
     user-driven page change. */
  const restoredCursorRef = useRef(Boolean(restoredEphemera?.cursor));
  const scrollRestoredRef = useRef(false);
  /* The return anchor (GDN-3197): captured from history state ONCE at mount,
     then stripped, so a later refresh doesn't re-flash a row the biller
     already found. Flash when the worked claim is still on this page;
     honest note when it left the view. */
  const location = useLocation();
  const [returnAnchor] = useState(() => readWorklistReturn(location.state));
  const [flashClaimId, setFlashClaimId] = useState<string | null>(null);
  const [leftViewNote, setLeftViewNote] = useState<string | null>(null);
  const returnConsumedRef = useRef(false);
  /* Mount barrier for the anchor: the mount refetch is DISPATCHED in the
     effects phase after the first committed render, so an effect running in
     that same flush still holds a closure where isFetching is false — and
     would consume a 15s+-stale cache before the in-flight state ever
     renders. State (not a ref) so a second render is guaranteed even when
     no refetch fires; from that render on, the refetch decision is
     visible. */
  const [anchorArmed, setAnchorArmed] = useState(false);
  useEffect(() => {
    setAnchorArmed(true);
  }, []);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [railCounts, setRailCounts] = useState<ClaimWorkCounts>();

  const defaultColumns = useMemo(() => getDefaultColumns(singularCap), [singularCap]);
  const { allColumns, updatePreferences, visibleColumns, visibleKeys } = useColumnPreferences(
    'claim-work',
    defaultColumns,
  );

  const serviceDate = selectedDateRange(predicates);
  const serviceDateWindowDays =
    serviceDate?.windowDays ??
    (serviceDate?.preset ? RELATIVE_DATE_WINDOWS[serviceDate.preset] : undefined);
  const currentConfig = useMemo<ClaimWorkSavedConfig>(
    () => ({
      filters: {
        claim_lifecycles: selectedValues(predicates, 'claimLifecycle'),
        patient_ids: selectedValues(predicates, 'patient'),
        payer_ids: selectedValues(predicates, 'payer'),
        provider_ids: selectedValues(predicates, 'provider'),
        service_date_window_days: serviceDateWindowDays ?? null,
        service_date_from: serviceDateWindowDays ? null : (serviceDate?.start ?? null),
        service_date_to: serviceDateWindowDays ? null : (serviceDate?.end ?? null),
      },
      sort,
      visible_columns: visibleKeys,
      work_view: workView,
    }),
    [predicates, serviceDate, serviceDateWindowDays, sort, visibleKeys, workView],
  );

  const queryArgs = useMemo(
    () => ({
      claimLifecycles: commaSeparated(selectedValues(predicates, 'claimLifecycle')),
      cursor,
      limit: perPage,
      patientIds: commaSeparated(selectedValues(predicates, 'patient')),
      payerIds: commaSeparated(selectedValues(predicates, 'payer')),
      providerIds: commaSeparated(selectedValues(predicates, 'provider')),
      serviceDateFrom: serviceDateWindowDays ? undefined : (serviceDate?.start ?? undefined),
      serviceDateTo: serviceDateWindowDays ? undefined : (serviceDate?.end ?? undefined),
      serviceDateWindowDays,
      sort,
      workView,
    }),
    [cursor, perPage, predicates, serviceDate, serviceDateWindowDays, sort, workView],
  );

  const { data, error, isFetching, isLoading, refetch } = useClaimWorkQuery(queryArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: 15,
    refetchOnReconnect: true,
  });
  const { data: savedViewData } = useClaimWorkSavedViewsQuery();
  const [createSavedView, createState] = useCreateClaimWorkSavedViewMutation();
  const [updateSavedView, updateState] = useUpdateClaimWorkSavedViewMutation();
  const [deleteSavedView, deleteState] = useDeleteClaimWorkSavedViewMutation();
  const {
    batchProgress,
    dismissBatch,
    isBatchActive,
    isBatchSubmitting,
    isBatchTerminal,
    submitBatch,
  } = useBatchClaimSubmit();
  /* Server truth per batched claim (queued/submitting/submitted/attention) —
     rows render from this; the work-state chip is never overwritten. */
  const batchRowStates = useMemo(
    () => deriveClaimBatchRowStates(batchProgress?.items),
    [batchProgress],
  );

  const savedViews = savedViewData?.views ?? [];
  const activeSavedView = savedViews.find(view => view.billing_saved_view_id === activeSavedViewId);
  /* Name hydration for the patient chip, keyed off the PREDICATE rather than
     the saved view: with the patient id living in the URL (GDN-3196), a
     reload or shared link restores the filter with no picker interaction
     behind it — the chip must still get a name, from any source of the id. */
  const urlPatientId = first(selectedValues(predicates, 'patient'));
  const { data: urlPatientData } = usePatientInfoQuery(
    { patientId: urlPatientId },
    { skip: !urlPatientId },
  );
  const dirty = Boolean(
    activeSavedView && normalizeConfig(activeSavedView.config) !== normalizeConfig(currentConfig),
  );
  const isSavedViewMutating =
    createState.isLoading || updateState.isLoading || deleteState.isLoading;

  useEffect(() => {
    setRailCounts(current => selectNewerClaimWorkCounts(current, data?.counts));
  }, [data?.counts]);

  useEffect(() => {
    if (!urlPatientId) {
      setSelectedPatient(current => (current ? undefined : current));
      return;
    }
    setSelectedPatient(current => {
      /* A picker-selected object for the same patient is already right —
         keep it rather than churning identity every fetch. */
      if (current && 'patient_id' in current && current.patient_id === urlPatientId) return current;
      if (urlPatientData) return mapPatientInfoResponseToPatient(urlPatientData, urlPatientId);
      /* The URL names a DIFFERENT patient than the chip shows and the new
         name hasn't arrived (pending, or the fetch failed) — clear NOW. A
         generic chip label while loading is honest; the old patient's name
         over a list filtered by the new id is not. */
      return current ? undefined : current;
    });
  }, [urlPatientData, urlPatientId]);

  useEffect(() => {
    if (!isBatchTerminal) return;
    refetch();
  }, [isBatchTerminal, refetch]);

  /* Ephemera persistence (GDN-3196): the cursor stack rides sessionStorage
     under the canonical-state signature; scroll is captured at leave time.
     Both best-effort by design. The derived cursor/cursorHistory are what's
     written, so a signature change persists page 1 for the NEW state rather
     than pairing the old stack with the new signature. */
  useEffect(() => {
    writeClaimWorkEphemera({ signature: stateSignature, cursor, cursorHistory });
  }, [cursor, cursorHistory, stateSignature]);
  useEffect(
    () => () => {
      updateClaimWorkEphemeraScroll(getAppScrollTop());
    },
    [],
  );
  useEffect(() => {
    if (scrollRestoredRef.current || !data) return;
    scrollRestoredRef.current = true;
    /* Through the app-scroller helper: the page scrolls inside RootView's
       container, not the window — window.scrollY reads ~0 here, which made
       this restore a silent no-op until GDN-3220. */
    if (restoredEphemera?.scrollY) scrollAppTo(restoredEphemera.scrollY);
  }, [data, restoredEphemera]);

  /* The restored cursor is confirmed by its first successful read — and if
     the server rejects it instead (expired token), the fallback is page 1 of
     the SAME filtered list, silently. Only a RESTORED cursor gets this
     treatment: a mid-session pagination error keeps the existing loud
     retry alert. */
  useEffect(() => {
    if (data) restoredCursorRef.current = false;
  }, [data]);
  useEffect(() => {
    if (!error || !restoredCursorRef.current) return;
    restoredCursorRef.current = false;
    setPageState({ signature: stateSignature, cursor: undefined, history: [] });
  }, [error, stateSignature]);

  const selectedPatientName = selectedPatient
    ? isPatientInfo(selectedPatient)
      ? (selectedPatient.name ?? '')
      : `${selectedPatient.first_name ?? ''} ${selectedPatient.last_name ?? ''}`.trim()
    : '';
  const filterFields = useMemo<FilterFieldDef[]>(
    () => [
      {
        icon: <LocalHospitalOutlinedIcon fontSize="inherit" />,
        id: 'provider',
        label: 'Provider',
        loading: cliniciansLoading,
        options: clinicians.map(clinician => ({ label: clinician.name, value: clinician.userId })),
        type: 'multiSelect',
      },
      {
        icon: <VerifiedUserOutlinedIcon fontSize="inherit" />,
        id: 'payer',
        label: 'Payer',
        loading: payersLoading,
        options: (payerData?.payers ?? []).map(payer => ({
          label: payer.payer_name,
          value: payer.payer_uuid,
        })),
        type: 'multiSelect',
      },
      {
        describeValue: value => {
          /* A saved view can carry several patient ids; the chip must say
             so rather than naming only the first while the list shows
             claims for all of them. The single-id fallback is the subject
             label, never the raw id. */
          const idCount = value.kind === 'multiSelect' ? value.values.length : 0;
          if (idCount > 1) return { op: 'is', value: `${idCount} ${plural}` };
          return { op: 'is', value: selectedPatientName || singularCap };
        },
        icon: <PersonOutlineOutlinedIcon fontSize="inherit" />,
        id: 'patient',
        label: singularCap,
        renderEditor: ({ autoFocus, onChange, onClose }) => (
          <Box sx={{ p: 1.5 }}>
            <PatientPicker
              autoFocus={autoFocus}
              disableAddNew
              initialPatient={selectedPatient}
              openOnFocus
              onPatientSelect={(patient: Patient | undefined) => {
                setSelectedPatient(patient);
                onChange(patient ? { kind: 'multiSelect', values: [patient.patient_id] } : null);
                if (patient) onClose();
              }}
              size="small"
            />
          </Box>
        ),
        type: 'custom',
      },
      {
        icon: <ReceiptLongOutlinedIcon fontSize="inherit" />,
        id: 'claimLifecycle',
        label: 'Claim status',
        options: CLAIM_LIFECYCLES.map(status => ({
          label: status
            .replaceAll('_', ' ')
            .replace(/^./, firstCharacter => firstCharacter.toUpperCase()),
          value: status,
        })),
        type: 'multiSelect',
      },
      {
        icon: <CalendarTodayOutlinedIcon fontSize="inherit" />,
        id: 'serviceDate',
        label: 'Service date',
        type: 'dateRange',
      },
    ],
    [
      clinicians,
      cliniciansLoading,
      payerData?.payers,
      payersLoading,
      plural,
      selectedPatient,
      selectedPatientName,
      singularCap,
    ],
  );

  /* Page 1 of whatever canonical state the pending URL write lands on — the
     signature recomputes on that render, so an explicitly-reset page state
     stays derived-valid either way. */
  const resetPage = useCallback(() => {
    restoredCursorRef.current = false;
    setPageState(current => ({ ...current, cursor: undefined, history: [] }));
    setSelectedIds(new Set());
  }, []);

  const updatePredicates = useCallback(
    (next: FiltersState) => {
      /* One event per field whose predicate actually changed — filter ids
         are a closed set (provider/payer/patient/claimLifecycle/serviceDate),
         values never leave the client. */
      const fieldIds = new Set([...Object.keys(predicates), ...Object.keys(next)]);
      for (const fieldId of fieldIds) {
        if (JSON.stringify(predicates[fieldId]) !== JSON.stringify(next[fieldId])) {
          billing.claimWorklistFilterChanged({ field_id: fieldId });
        }
      }
      writeUrlState({ predicates: next });
      if (selectedValues(next, 'patient').length === 0) setSelectedPatient(undefined);
      resetPage();
    },
    [billing, predicates, resetPage, writeUrlState],
  );

  const clearPredicates = useCallback(() => {
    billing.claimWorklistFilterChanged({ field_id: 'clear_all' });
    writeUrlState({ predicates: {} });
    setSelectedPatient(undefined);
    resetPage();
  }, [billing, resetPage, writeUrlState]);

  const resetPredicates = useCallback(() => {
    billing.claimWorklistFilterChanged({ field_id: 'reset' });
    writeUrlState({ predicates: createDefaultClaimWorkPredicates() });
    setSelectedPatient(undefined);
    resetPage();
  }, [billing, resetPage, writeUrlState]);

  const selectWorkView = useCallback(
    (value: ClaimWorkViewKey) => {
      billing.claimWorklistWorkViewSelected({ work_view: value });
      /* ONE URL write per interaction — the handlers never stack
         setSearchParams calls, so the replace discipline holds and no write
         can read another's stale params. */
      writeUrlState({
        workView: value,
        savedViewId: null,
        ...(activeSavedViewId
          ? { predicates: createDefaultClaimWorkPredicates(), sort: 'service_date_desc' as const }
          : {}),
      });
      if (activeSavedViewId) setSelectedPatient(undefined);
      resetPage();
    },
    [activeSavedViewId, billing, resetPage, writeUrlState],
  );

  const applySavedView = useCallback(
    (view: BillingSavedView) => {
      billing.claimWorklistSavedViewSelected({ saved_view_id: view.billing_saved_view_id });
      writeUrlState({
        savedViewId: view.billing_saved_view_id,
        workView: view.config.work_view ?? 'all_claim_work',
        sort: view.config.sort ?? 'service_date_desc',
        predicates: filtersFromConfig(view.config),
      });
      /* Columns are part of the saved view. Leaving the local column
         preferences active marked a freshly opened view dirty and let
         Update overwrite the organization's saved columns with the local
         set. (The patient chip's name hydrates from the predicate effect —
         same path as a reload.) */
      const nextColumns = savedViewVisibleColumns(view.config.visible_columns, allColumns);
      if (nextColumns) updatePreferences(nextColumns, allColumns);
      resetPage();
    },
    [allColumns, billing, resetPage, updatePreferences, writeUrlState],
  );

  const saveCurrentView = useCallback(async () => {
    try {
      const created = await createSavedView({
        createBillingSavedViewRequest: {
          config: currentConfig,
          definition_version: 1,
          name: saveName.trim(),
        },
      }).unwrap();
      billing.claimWorklistSavedViewSubmitted({
        saved_view_id: created.billing_saved_view_id,
        action: 'create',
      });
      writeUrlState({ savedViewId: created.billing_saved_view_id });
      setSaveDialogOpen(false);
      setSaveName('');
      enqueueSnackbar(`Saved “${created.name}” for this organization.`, { variant: 'success' });
    } catch {
      enqueueSnackbar('The view could not be saved. Refresh the list and try again.', {
        variant: 'error',
      });
    }
  }, [billing, createSavedView, currentConfig, enqueueSnackbar, saveName, writeUrlState]);

  const updateCurrentView = useCallback(async () => {
    if (!activeSavedView) return;
    try {
      await updateSavedView({
        savedViewId: activeSavedView.billing_saved_view_id,
        updateBillingSavedViewRequest: {
          config: currentConfig,
          definition_version: activeSavedView.definition_version ?? 1,
          expected_version: activeSavedView.version ?? 1,
          name: activeSavedView.name,
        },
      }).unwrap();
      billing.claimWorklistSavedViewSubmitted({
        saved_view_id: activeSavedView.billing_saved_view_id,
        action: 'update',
      });
      enqueueSnackbar(`Updated “${activeSavedView.name}” for this organization.`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('This view changed elsewhere. Refresh it before updating.', {
        variant: 'error',
      });
    }
  }, [activeSavedView, billing, currentConfig, enqueueSnackbar, updateSavedView]);

  const deleteCurrentView = useCallback(
    async (view: BillingSavedView) => {
      /* "_requested" is the ask, fired before the outcome — a version
         conflict still counts as the biller trying to delete. */
      billing.claimWorklistSavedViewDeleteRequested({
        saved_view_id: view.billing_saved_view_id,
      });
      try {
        await deleteSavedView({
          expectedVersion: view.version ?? 1,
          savedViewId: view.billing_saved_view_id,
        }).unwrap();
        writeUrlState({ savedViewId: null });
        enqueueSnackbar(`Deleted “${view.name}”.`, { variant: 'success' });
      } catch {
        enqueueSnackbar('This view changed elsewhere. Refresh it before deleting.', {
          variant: 'error',
        });
      }
    },
    [billing, deleteSavedView, enqueueSnackbar, writeUrlState],
  );

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  /* The anchor, resolved against a SETTLED read: flash the row when
     the worked claim is still in view (it returns to the same page via the
     GDN-3196 cursor restore), or say honestly that it left — with the state
     the workbench last knew from work_summary, never a guess.

     `!isFetching` matters: a remount can serve CACHED items while the mount
     refetch is still in flight, and consuming the anchor against that stale
     page could flash a row about to vanish or claim a departure the fresh
     read disproves — the exact dishonesty this anchor exists to avoid. When
     no refetch fires, the cache is within the list's own freshness window
     and is the settled answer. `!error` for the same reason: a FAILED
     refetch settles onto the stale cache — better no anchor than a wrong
     one, and bailing un-consumed lets a successful retry still speak. */
  useEffect(() => {
    if (!anchorArmed || !returnAnchor || returnConsumedRef.current || !data || isFetching || error)
      return;
    returnConsumedRef.current = true;
    /* The history-state strip happens HERE, at consumption — not at mount.
       Stripping earlier would discard an anchor the error guard left
       unconsumed, so a refresh during a failed-fetch window would lose the
       flash/note the eventual successful read should deliver. Consumed →
       stripped → a later refresh doesn't replay; unconsumed → the anchor
       survives the refresh. */
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    const present = items.some(item => item.claim_id === returnAnchor.returnedClaimId);
    if (present) {
      setFlashClaimId(returnAnchor.returnedClaimId);
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      document
        .querySelector(`[data-row-id="${CSS.escape(returnAnchor.returnedClaimId)}"]`)
        ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      /* No cleanup on purpose: a mid-flash refetch re-runs this effect, and
         a cleanup would cancel the fade early. The consumed ref makes the
         timer a one-shot. */
      setTimeout(() => setFlashClaimId(null), 2600);
      return;
    }
    setLeftViewNote(
      returnAnchor.returnedStateLabel
        ? `The claim you worked on is now ${returnAnchor.returnedStateLabel} — it left this view.`
        : 'The claim you worked on is no longer in this view.',
    );
  }, [
    anchorArmed,
    data,
    error,
    isFetching,
    items,
    location.pathname,
    location.search,
    navigate,
    returnAnchor,
  ]);

  const rows = useMemo(
    () =>
      items.map(item => ({
        _raw: item,
        amount: formatCents(item.total_charge_cents ?? 0),
        claim_id: item.claim_id,
        id: item.claim_id,
        issues: item.active_finding_count ?? 0,
        patient_id: item.patient_id,
        patient_name: item.patient_name ?? '—',
        payer_name: item.payer_name ?? '—',
        provider_name: item.clinician_display_name ?? '—',
        service_date: formatDate(item.service_date),
        work_state: item.work_state,
      })),
    [items],
  );

  const renderCell = useCallback(
    (column: ColumnDef, row: Record<string, unknown>) => {
      const item = row._raw as ClaimWorkItem;
      if (column.key === 'patient_name') {
        return <PatientIdentity patientId={item.patient_id} patientName={item.patient_name} />;
      }
      if (column.key === 'provider_name') {
        /* The rendering provider — the person who saw the patient — not
           the billing identity this column used to show (GDN-3243). The
           name goes through providerName (NOT badgeName) so the badge's
           initials and hashed color derive from the same person the text
           names. provider_id is that same person server-side
           (models/claims.py, GDN-1926), so id and name agree by
           construction.

           An empty cell is HONEST ABSENCE, not a failure: imported claims
           mostly arrive without one (ADR 0147). It says so in words and
           never falls back to the billing identity. */
        return (
          <ProviderIdentity
            missingLabel="No provider recorded"
            providerId={item.provider_id}
            providerName={item.clinician_display_name}
          />
        );
      }
      /* Rows in the running batch tell their submission status in the issues
         column — the server's per-claim word, live (GDN-3216). Only this
         column: the work-state chip stays the claim's own truth, and it is
         the batch's terminal invalidation, not this override, that updates it. */
      const batchState = batchRowStates.get(item.claim_id);
      if (column.key === 'issues' && batchState) {
        if (batchState.inFlight) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CircularProgress size={14} />
              <Typography color="text.secondary" variant="caption">
                {batchState.status === 'queued' ? 'Queued' : 'Submitting…'}
              </Typography>
            </Stack>
          );
        }
        if (batchState.attention) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                {batchState.status === 'blocked' ? 'Blocked before send' : 'Needs attention'}
              </Typography>
            </Stack>
          );
        }
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
            <Typography color="text.secondary" variant="caption">
              Sent to clearinghouse
            </Typography>
          </Stack>
        );
      }
      if (column.key === 'issues') {
        /* Real findings render for ANY stage — payer-side content is not
           pre-flight advice. Everything below that is (GDN-3242). */
        const firstFinding = item.finding_highlights?.[0];
        const findingText =
          firstFinding?.why ?? item.submission_top_blocker ?? item.legacy_top_blocker;
        /* Certification copy is pre-flight advice: it renders only while the
           biller can still shape a (re)submission. On in-transit and
           payer-side rows the column says "nothing applies" — a dash, not an
           empty cell that could read as a load failure.
           Deliberately ABOVE the `checking` branch: a certification run in
           flight on a submitted claim cannot change what the payer already
           received, so announcing "Checking claim" there would be the same
           pre-flight noise in spinner form. */
        if (!findingText && !isWorkableClaimStage(item.work_state)) {
          return (
            <Typography
              color="text.disabled"
              data-testid="cert-copy-not-applicable"
              variant="body2"
            >
              —
            </Typography>
          );
        }
        if (item.certification_run_state === 'checking') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Checking claim</Typography>
            </Stack>
          );
        }
        if (!findingText && item.certification_freshness === 'stale') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="warning" sx={{ fontSize: 16 }} />
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                  Certification is stale
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  Recheck before relying on it
                </Typography>
              </Box>
            </Stack>
          );
        }
        if (!findingText && item.certification_freshness === 'missing') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="warning" sx={{ fontSize: 16 }} />
              <Typography color="text.secondary" variant="caption">
                Not checked yet
              </Typography>
            </Stack>
          );
        }
        if (!findingText) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
              <Typography color="text.secondary" variant="caption">
                Certified · no active findings
              </Typography>
            </Stack>
          );
        }
        const issueCount = firstFinding
          ? `${item.active_finding_count ?? 0} finding${item.active_finding_count === 1 ? '' : 's'}`
          : item.submission_top_blocker
            ? `${item.submission_blocker_count ?? 0} submission issue${item.submission_blocker_count === 1 ? '' : 's'}`
            : `${item.legacy_error_count ?? 0} validation issue${item.legacy_error_count === 1 ? '' : 's'}`;
        return (
          <Stack direction="row" spacing={0.75} sx={{ minWidth: 0 }}>
            <ErrorOutlineIcon
              color={item.active_blocker_count ? 'error' : 'warning'}
              sx={{ flexShrink: 0, fontSize: 16, mt: 0.15 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                {findingText}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {issueCount}
                {/* The findings branch is exempt from the stage gate — payer
                    content renders at any stage — but this SUFFIX is
                    pre-flight advice riding along with it, so it carries the
                    gate itself. A denial on a with_payer claim can sit beside
                    a stale local certification snapshot; telling the biller
                    to recheck it is the same misleading counsel this ticket
                    removes, just spelled differently (GDN-3242). */}
                {item.certification_freshness === 'stale' && isWorkableClaimStage(item.work_state)
                  ? ' · needs recheck'
                  : ''}
              </Typography>
            </Box>
          </Stack>
        );
      }
      if (column.key === 'work_state') {
        const stateLabel =
          item.work_state === 'needs_correction' &&
          ['denied', 'rejected', 'reversed'].includes(item.payer_outcome)
            ? item.payer_outcome.charAt(0).toUpperCase() + item.payer_outcome.slice(1)
            : (WORK_STATE_LABELS[item.work_state] ?? item.work_state);
        return (
          <Chip
            color={WORK_STATE_COLORS[item.work_state] ?? 'default'}
            label={stateLabel}
            size="small"
            variant={item.work_state === 'draft' ? 'outlined' : 'filled'}
            sx={{ fontWeight: 600 }}
          />
        );
      }
      return null;
    },
    [batchRowStates],
  );

  const selectedItems = items.filter(item => selectedIds.has(item.claim_id));
  const readySelectedIds = selectedItems.filter(item => item.can_submit).map(item => item.claim_id);
  const bulkActions = useMemo<SelectionAction[]>(
    () => [
      {
        disabled: readySelectedIds.length !== selectedIds.size,
        label:
          readySelectedIds.length === selectedIds.size
            ? `Submit ${readySelectedIds.length} claim${readySelectedIds.length === 1 ? '' : 's'}`
            : 'Selection includes blocked claims',
        loading: isBatchSubmitting,
        onClick: async () => {
          /* Fired at the ask — per-claim outcomes land through the shared
             submit event when the batch reaches a terminal state. */
          billing.claimBatchSubmitInitiated({ claim_count: readySelectedIds.length });
          try {
            await submitBatch(readySelectedIds);
            setSelectedIds(new Set());
            enqueueSnackbar(
              `${readySelectedIds.length} claim${readySelectedIds.length === 1 ? '' : 's'} queued for submission.`,
              { variant: 'success' },
            );
          } catch {
            enqueueSnackbar(
              'The batch could not start. The selected claims have not been submitted.',
              {
                variant: 'error',
              },
            );
          }
        },
      },
    ],
    [billing, enqueueSnackbar, isBatchSubmitting, readySelectedIds, selectedIds.size, submitBatch],
  );

  const duplicateName = savedViews.some(
    view => view.name.trim().toLowerCase() === saveName.trim().toLowerCase(),
  );
  const serviceDateField = filterFields.find(field => field.id === 'serviceDate');
  const dateScopeLabel =
    serviceDate && serviceDateField
      ? describeFilter(serviceDateField, serviceDate).value
      : 'All time';
  const isDefaultPredicateSet =
    JSON.stringify(predicates) === JSON.stringify(createDefaultClaimWorkPredicates());
  const currentPage = cursorHistory.length + 1;
  const matchingCount = data?.counts.matching_count ?? 0;

  /**
   * Straight to the workbench (GDN-3199): the row already carries
   * `encounter_id` (canonical ClaimWorkItem), so routing through the
   * `/jotbill/claim/:id` gate shim — which fetches the claim JUST to learn
   * the encounter id, then replace-redirects — pays a full sequential round
   * trip before the workbench's own 2.35s load even starts. Carrying
   * `claim_id` in the URL also moves the certification and claim-record
   * queries into the FIRST fetch wave. The gate route stays as the fallback
   * door for rows without an encounter id and for external links.
   */
  const { prefetchRow, wasPrefetched } = useWorklistRowPrefetch();

  const openClaim = useCallback(
    (item: ClaimWorkItem) => {
      billing.claimWorklistRowClicked({
        claim_id: item.claim_id,
        /* The prefetch's own meter (GDN-3201): clicks on warmed rows vs
           cold ones — paired with workbench_entered latency, the payoff. */
        prefetched: wasPrefetched(item.claim_id),
        route: item.encounter_id ? 'direct' : 'gate_fallback',
      });
      /* Provenance (GDN-3197): the current URL IS the saved list state
         (GDN-3196), so the workbench's back button can say "Claims" and
         return to this exact list. Rides through the gate route too — the
         redirect preserves location.state. */
      navigate(
        item.encounter_id
          ? `/jotbill/encounter/${item.encounter_id}?workspace=claim&claim_id=${item.claim_id}`
          : `/jotbill/claim/${item.claim_id}`,
        {
          state: workbenchProvenanceState({
            url: `/jotbill?${searchParams.toString()}`,
            label: 'Claims',
          }),
        },
      );
    },
    [billing, navigate, searchParams, wasPrefetched],
  );

  const changePage = (nextPage: number) => {
    restoredCursorRef.current = false;
    if (nextPage === currentPage + 1 && data?.next_cursor) {
      setPageState({
        signature: stateSignature,
        cursor: data.next_cursor,
        history: [...cursorHistory, cursor],
      });
      setSelectedIds(new Set());
      return;
    }
    if (nextPage === currentPage - 1 && cursorHistory.length > 0) {
      const history = [...cursorHistory];
      const previous = history.pop();
      setPageState({ signature: stateSignature, cursor: previous, history });
      setSelectedIds(new Set());
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '220px 1fr' } }}>
        <Skeleton height={340} variant="rounded" />
        <Skeleton height={520} variant="rounded" />
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid="claim-work-list"
        sx={{
          alignItems: 'start',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '220px minmax(0, 1fr)' },
        }}
      >
        <ClaimWorkViewRail
          activeSavedViewId={activeSavedViewId}
          activeWorkView={workView}
          counts={(railCounts ?? data?.counts)?.work_view_counts ?? {}}
          dirty={dirty}
          isMutating={isSavedViewMutating}
          savedViews={savedViews}
          onDelete={deleteCurrentView}
          onSave={() => setSaveDialogOpen(true)}
          onSelectSavedView={applySavedView}
          onSelectWorkView={selectWorkView}
          onUpdate={updateCurrentView}
        />

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Card variant="outlined">
            <CardContent sx={{ '&:last-child': { pb: 1.5 }, p: 1.5 }}>
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                  {activeSavedView?.name ?? WORK_VIEWS.find(view => view.key === workView)?.label}
                </Typography>
                <Divider />
                <FilterBar
                  canClearPredicates={Object.keys(predicates).length > 0}
                  canResetView={activeSavedViewId === null && !isDefaultPredicateSet}
                  fields={filterFields}
                  predicates={predicates}
                  resultStatement={`${matchingCount} matching claim${matchingCount === 1 ? '' : 's'} · ${dateScopeLabel}`}
                  utilityControl={
                    <SortMenuButton
                      options={SORTS.map(option => ({ label: option.label, value: option.key }))}
                      value={sort}
                      onChange={nextSort => {
                        billing.claimWorklistSortChanged({ sort: nextSort });
                        writeUrlState({ sort: nextSort });
                        resetPage();
                      }}
                    />
                  }
                  onClearPredicates={clearPredicates}
                  onPredicatesChange={updatePredicates}
                  onResetView={resetPredicates}
                />
              </Stack>
            </CardContent>
          </Card>

          {error && (
            <Alert
              action={
                <Button color="inherit" onClick={() => refetch()} size="small">
                  Retry
                </Button>
              }
              severity="error"
            >
              Claim work could not load. The canonical work relation may not be available yet.
            </Alert>
          )}

          {leftViewNote && (
            <Alert onClose={() => setLeftViewNote(null)} severity="info">
              {leftViewNote}
            </Alert>
          )}

          <Box sx={{ opacity: isFetching ? 0.72 : 1, transition: 'opacity 120ms ease' }}>
            <DataTable
              columns={visibleColumns}
              getRowSelectionLabel={row => `Select claim for ${String(row.patient_name)}`}
              /* The one action surface (GDN-3216): selection and batch progress
                 live in the table's own header, so nothing floats over rows and
                 the last row is always reachable. */
              headerOverlay={
                selectedIds.size > 0 || batchProgress ? (
                  <ClaimBulkActionSlot
                    actions={bulkActions}
                    batches={batchProgress ? [batchProgress] : []}
                    onClearSelection={() => {
                      billing.claimWorklistClearSelectionClicked({
                        selected_count: selectedIds.size,
                      });
                      setSelectedIds(new Set());
                    }}
                    onDismissBatch={() => {
                      billing.claimBatchDismissClicked({
                        batch_status: batchProgress?.status ?? 'unknown',
                      });
                      dismissBatch();
                    }}
                    onReviewClaim={claimId => {
                      billing.claimBatchReviewClicked({
                        attention_count: [...batchRowStates.values()].filter(s => s.attention)
                          .length,
                        claim_id: claimId,
                      });
                      navigate(`/jotbill/claim/${claimId}`);
                    }}
                    selectedCount={selectedIds.size}
                  />
                ) : undefined
              }
              idKey="claim_id"
              isFetching={isFetching}
              /* isBatchActive, not activeBatchId: a TERMINAL batch keeps its
                 summary on screen but must not keep the worklist locked —
                 selection reopens the moment the server says the run ended. */
              isRowSelectable={row => (row._raw as ClaimWorkItem).can_submit && !isBatchActive}
              onRowClick={row => openClaim(row._raw as ClaimWorkItem)}
              onRowHover={row => {
                const item = row._raw as ClaimWorkItem;
                /* Binding constraint from the selection round (GDN-3216):
                   rows in an ACTIVE batch are mid-submission — a workbench
                   read now would race the drain. Skipping here (not inside
                   the hook) leaves the row eligible again at terminal:
                   nothing was marked as seen. Before the first progress
                   poll lands, membership is UNKNOWN — skip every row for
                   that sub-second window rather than guess. */
                if (
                  isBatchActive &&
                  (batchRowStates.size === 0 || batchRowStates.has(item.claim_id))
                )
                  return;
                prefetchRow(item);
              }}
              onSelectionChange={setSelectedIds}
              renderCell={renderCell}
              rows={rows}
              rowSelectionDisabledReason={
                isBatchActive ? 'A submission batch is finishing' : undefined
              }
              stickyHeaderTop={jotBillStickyTop(0)}
              /* The returned-to row flashes (GDN-3197); under
                 prefers-reduced-motion it holds a static tint that clears
                 when the flash window ends. Colors ride the theme's primary,
                 not literals. */
              rowSx={row => {
                const batchState = batchRowStates.get(row.claim_id as string);
                return {
                  /* In-flight batch rows dim; attention rows carry an inset
                     error edge that pairs with the slot's "Review N claims"
                     (GDN-3216). Inset shadow, so row geometry never shifts. */
                  ...(batchState?.inFlight ? { opacity: 0.55 } : {}),
                  ...(batchState?.attention
                    ? { boxShadow: `inset 3px 0 0 ${theme.palette.error.main}` }
                    : {}),
                  ...(row.claim_id === flashClaimId
                    ? {
                        '@keyframes claimWorkRowFlash': {
                          from: { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
                          to: { backgroundColor: 'transparent' },
                        },
                        animation: 'claimWorkRowFlash 2.4s ease-out',
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }
                    : {}),
                };
              }}
              selectedIds={selectedIds}
            />
          </Box>

          <Pagination
            data={{
              page: currentPage,
              per_page: perPage,
              total: matchingCount,
              total_pages: Math.max(1, Math.ceil(matchingCount / perPage)),
            }}
            onPageChange={changePage}
            onPerPageChange={nextPerPage => {
              writeUrlState({ perPage: nextPerPage });
              resetPage();
            }}
          />
        </Stack>
      </Box>

      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setSaveDialogOpen(false)}
        open={saveDialogOpen}
      >
        <DialogTitle>Save organization view</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              error={duplicateName}
              helperText={
                duplicateName
                  ? 'A saved view already uses this name.'
                  : 'Everyone in this organization will see this view.'
              }
              label="View name"
              onChange={event => setSaveName(event.target.value)}
              value={saveName}
            />
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label={WORK_VIEWS.find(view => view.key === workView)?.label} size="small" />
              <Chip label={SORTS.find(option => option.key === sort)?.label} size="small" />
              <Chip label={`${Object.keys(predicates).length} filters`} size="small" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            disabled={!saveName.trim() || duplicateName || createState.isLoading}
            onClick={saveCurrentView}
            startIcon={
              createState.isLoading ? <CircularProgress size={14} /> : <SaveOutlinedIcon />
            }
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            Save view
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
